import { Node, mergeAttributes } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';
import CollapsibleContentComponent from './CollapsibleContentComponent';

export interface CollapsibleContentOptions {
  HTMLAttributes: Record<string, any>;
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    collapsibleContent: {
      /**
       * Set a collapsible content block
       */
      setCollapsibleContent: (attributes?: { isCollapsed?: boolean }) => ReturnType;
      /**
       * Toggle collapsible content
       */
      toggleCollapsibleContent: () => ReturnType;
      /**
       * Unset collapsible content
       */
      unsetCollapsibleContent: () => ReturnType;
    };
  }
}

export const CollapsibleContent = Node.create<CollapsibleContentOptions>({
  name: 'collapsibleContent',

  addOptions() {
    return {
      HTMLAttributes: {},
    };
  },

  group: 'block',

  content: 'block+',

  addAttributes() {
    return {
      isCollapsed: {
        default: true,
        parseHTML: element => {
          return element.getAttribute('data-collapsed') === 'true';
        },
        renderHTML: attributes => {
          return {
            'data-collapsed': attributes.isCollapsed,
          };
        },
      },
      originalContent: {
        default: '',
        parseHTML: element => {
          return element.getAttribute('data-original-content') || '';
        },
        renderHTML: attributes => {
          return {
            'data-original-content': attributes.originalContent,
          };
        },
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'div[data-collapsible-content]',
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'div',
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, {
        'data-collapsible-content': '',
        class: 'collapsible-content',
      }),
      0,
    ];
  },

  addCommands() {
    return {
      setCollapsibleContent:
        (attributes = {}) =>
        ({ commands }) => {
          return commands.setNode(this.name, attributes);
        },
      toggleCollapsibleContent:
        () =>
        ({ commands, state }) => {
          const { selection } = state;
          const { $from } = selection;
          const node = $from.node();
          
          if (node.type.name === this.name) {
            const currentAttributes = node.attrs;
            return commands.updateAttributes(this.name, {
              isCollapsed: !currentAttributes.isCollapsed,
            });
          }
          
          return false;
        },
      unsetCollapsibleContent:
        () =>
        ({ commands }) => {
          return commands.deleteNode(this.name);
        },
    };
  },

  addNodeView() {
    return ReactNodeViewRenderer(CollapsibleContentComponent);
  },
});
